// import node module libraries
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ChatMessage } from "@/types";

interface SuggestedTemplateQuestions {
  q_id: string;
  question: string;
}

interface ChatSuggestProps {
  id: string;
  question: string;
}

interface ChatSuggestionProps {
  q_id: string;
  question: string;
}

interface ChatState {
  isChatStarted: boolean;
  isChatLoading: boolean;
  suggestedQuestions: ChatSuggestProps[];
  suggestedTemplates: SuggestedTemplateQuestions[];
  chatSuggestion: ChatSuggestionProps[];
  isQuestionLoading: boolean;
  isQuestionVisible: boolean;
  isTemplateLoading: boolean;
  isTemplateVisible: boolean;
  chat_history: ChatMessage[];
  activeChatId: string | null;
  isSampleData: boolean;
  selectedText?: string | null;
  /** When true, [chat_id] page must not restore activeChatId (user clicked "New Chat") */
  isNewChatIntent: boolean;
}

const initialState: ChatState = {
  isChatStarted: false,
  isChatLoading: false,
  suggestedQuestions: [],
  suggestedTemplates: [],
  chatSuggestion: [],
  isQuestionLoading: false,
  isQuestionVisible: false,
  isTemplateLoading: false,
  isTemplateVisible: false,
  chat_history: [],
  activeChatId: null,
  isSampleData: false,
  selectedText: null,
  isNewChatIntent: false,
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setChatLoading: (state, action: PayloadAction<boolean>) => {
      state.isChatLoading = action.payload;
    },
    chatStarted: (state) => {
      state.isChatStarted = true;
    },
    chatEnd: (state) => {
      state.isChatStarted = false;
    },

    setSuggestedQuestion: (
      state,
      action: PayloadAction<ChatSuggestProps[]>
    ) => {
      state.suggestedQuestions = action.payload;
      state.isQuestionVisible = true;
    },
    setSuggestedQuestionLoading: (state, action: PayloadAction<boolean>) => {
      state.isQuestionLoading = action.payload;
    },
    setSuggestedTemplates: (
      state,
      action: PayloadAction<SuggestedTemplateQuestions[]>
    ) => {
      state.suggestedTemplates = action.payload;
      state.isTemplateVisible = true;
    },
    showSuggestedQuestion: (state, action: PayloadAction<boolean>) => {
      state.isQuestionVisible = action.payload;
    },
    showSuggestedTemplate: (state, action: PayloadAction<boolean>) => {
      state.isTemplateVisible = action.payload;
    },

    setSuggestedTemplateLoading: (state, action: PayloadAction<boolean>) => {
      state.isTemplateLoading = action.payload;
    },
    setChatSuggestion: (
      state,
      action: PayloadAction<ChatSuggestionProps[]>
    ) => {
      state.chatSuggestion = action.payload;
    },
    addChatBId: (state, action: PayloadAction<ChatMessage[]>) => {
      state.chat_history = action.payload;
    },
    addMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.chat_history.push(action.payload);
    },
    resetChats: (state) => {
      state.chat_history = [];
      state.activeChatId = null;
    },
    updateChatItem: (
      state,
      action: PayloadAction<{ updateData: ChatMessage }>
    ) => {
      const { id, ...updateMessage } = action.payload.updateData;
      const existingChatIndex = state.chat_history.findIndex(
        (item) => item.id === id
      );

      if (existingChatIndex !== -1) {
        state.chat_history[existingChatIndex] = {
          ...state.chat_history[existingChatIndex],
          ...updateMessage,
        };
      }
    },
    setChatLike: (
      state,
      action: PayloadAction<{ id: string; isLiked: number }>
    ) => {
      const { id, isLiked } = action.payload;

      const existingChatIndex = state.chat_history.findIndex(
        (item) => item.id === id
      );

      if (existingChatIndex !== -1) {
        state.chat_history[existingChatIndex] = {
          ...state.chat_history[existingChatIndex],
          is_liked: isLiked,
        };
      }
    },
    setActiveChatId: (
      state,
      action: PayloadAction<{ chat_id: string | null }>
    ) => {
      state.activeChatId = action.payload.chat_id;
    },
    setIsSampleData: (state, action: PayloadAction<boolean>) => {
      state.isSampleData = action.payload;
    },
    setSelectedText: (state, action: PayloadAction<string | null>) => {
      state.selectedText = action.payload;
    },

    setNewChatIntent: (state, action: PayloadAction<boolean>) => {
      state.isNewChatIntent = action.payload;
    },

    resetAllChat: (state) => ({
      ...initialState,
      isNewChatIntent: state.isNewChatIntent,
    }),
  },
});

export const {
  resetAllChat,
  setChatLoading,
  setNewChatIntent,
  setSuggestedQuestion,
  showSuggestedQuestion,
  showSuggestedTemplate,
  setSuggestedTemplates,
  chatStarted,
  setActiveChatId,
  chatEnd,
  setSuggestedTemplateLoading,
  setSuggestedQuestionLoading,
  setChatSuggestion,
  addMessage,
  addChatBId,
  resetChats,
  updateChatItem,
  setChatLike,
  setIsSampleData,
  setSelectedText,
} = chatSlice.actions;

export default chatSlice.reducer;
